/**
 * Hooks for the Find an Accountant directory.
 *
 * useFindAccountants: filtered/sorted list for the directory page.
 * useAccountantById: single accountant lookup for the profile page.
 *
 * Both return the Novala data-architecture shape. Phase A reads from
 * mock data; Phase B swaps for a real partner-accountant backend by
 * editing this file only, no component changes.
 */

import { useCallback, useMemo, useState } from "react";
import type {
  DirectoryAccountant,
  FindAccountantFilters,
  FindSortField,
} from "../pages/Accounting/MyAccountant/findAccountant/findAccountantTypes";
import { DEFAULT_DISTANCE_KM } from "../pages/Accounting/MyAccountant/findAccountant/findAccountantTypes";
import { MOCK_DIRECTORY_ACCOUNTANTS } from "../pages/Accounting/MyAccountant/findAccountant/findAccountantMockData";

function createEmptyFilters(): FindAccountantFilters {
  return {
    location: "",
    distanceKm: DEFAULT_DISTANCE_KM,
    services: [],
    industry: "",
    products: [],
  };
}

function applyFilters(
  accountants: DirectoryAccountant[],
  filters: FindAccountantFilters,
): DirectoryAccountant[] {
  const needleLocation = filters.location.trim().toLowerCase();
  return accountants.filter((a) => {
    if (needleLocation) {
      const haystack = [a.city, a.province, a.postalCode ?? ""]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(needleLocation)) return false;
    }
    if (a.distanceKm > filters.distanceKm) return false;
    if (filters.services.length > 0) {
      const hasAll = filters.services.every((s) => a.services.includes(s));
      if (!hasAll) return false;
    }
    if (filters.industry) {
      if (!a.industries.includes(filters.industry)) return false;
    }
    if (filters.products.length > 0) {
      const hasAll = filters.products.every((p) => a.products.includes(p));
      if (!hasAll) return false;
    }
    return true;
  });
}

function applySort(
  accountants: DirectoryAccountant[],
  field: FindSortField,
): DirectoryAccountant[] {
  const copy = accountants.slice();
  switch (field) {
    case "distance":
      copy.sort((a, b) => a.distanceKm - b.distanceKm);
      break;
    case "experience":
      copy.sort((a, b) => b.yearsExperience - a.yearsExperience);
      break;
    case "name":
      copy.sort((a, b) => a.name.localeCompare(b.name));
      break;
  }
  return copy;
}

export interface UseFindAccountantsResult {
  data: DirectoryAccountant[];
  loading: boolean;
  error: Error | null;
  filters: FindAccountantFilters;
  setFilters: (patch: Partial<FindAccountantFilters>) => void;
  resetFilters: () => void;
  sortField: FindSortField;
  setSortField: (field: FindSortField) => void;
  refetch: () => void;
}

export function useFindAccountants(): UseFindAccountantsResult {
  const [filters, setFiltersState] = useState<FindAccountantFilters>(() =>
    createEmptyFilters(),
  );
  const [sortField, setSortField] = useState<FindSortField>("distance");
  const [error] = useState<Error | null>(null);
  const loading = false;

  const setFilters = useCallback(
    (patch: Partial<FindAccountantFilters>) => {
      setFiltersState((prev) => ({ ...prev, ...patch }));
    },
    [],
  );

  const resetFilters = useCallback(() => {
    setFiltersState(createEmptyFilters());
  }, []);

  const refetch = useCallback(() => {}, []);

  const data = useMemo(
    () => applySort(applyFilters(MOCK_DIRECTORY_ACCOUNTANTS, filters), sortField),
    [filters, sortField],
  );

  return {
    data,
    loading,
    error,
    filters,
    setFilters,
    resetFilters,
    sortField,
    setSortField,
    refetch,
  };
}

export interface UseAccountantByIdResult {
  data: DirectoryAccountant | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useAccountantById(id: string | undefined): UseAccountantByIdResult {
  const [error] = useState<Error | null>(null);
  const loading = false;

  const refetch = useCallback(() => {}, []);

  const data = useMemo(() => {
    if (!id) return null;
    return MOCK_DIRECTORY_ACCOUNTANTS.find((a) => a.id === id) ?? null;
  }, [id]);

  return { data, loading, error, refetch };
}